import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import './styles/index.css'
import './i18n'
import { AuthProvider } from '@/providers/AuthProvider'
import { ContextProvider } from '@/providers/ContextProvider'
import { PermissionProvider } from '@/providers/PermissionProvider'
import { LiveKitProvider } from '@/providers/LiveKitProvider'
import ThemeProvider from '@/providers/ThemeProvider'
import { loadRuntimeConfig } from '@/config/runtime'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on rate-limit (429) or conflict (409) errors
        const status = (error as any)?.status ?? (error as any)?.response?.status
        if (status === 429 || status === 409) return false
        return failureCount < 1
      },
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

function getRouterBasename(baseUrl: string): string {
  const normalizedBase = baseUrl.trim()
  if (!normalizedBase || normalizedBase === '/' || normalizedBase === './' || normalizedBase === '.') {
    return '/'
  }

  return normalizedBase.replace(/\/$/, '')
}

function renderApp() {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          basename={getRouterBasename(import.meta.env.BASE_URL)}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ThemeProvider>
            <AuthProvider>
              <ContextProvider>
                <PermissionProvider>
                  <LiveKitProvider>
                    <App />
                  </LiveKitProvider>
                </PermissionProvider>
              </ContextProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

void loadRuntimeConfig().finally(renderApp)
